import { z } from 'zod'
import { SEGMENT_NAMES } from './segments'

/**
 * FR-MKT-2 — audiences as data.
 *
 * A campaign's audience is a stored filter, not code: the same predicate
 * definition drives the preview count, the send, and the segment explorer, so
 * the number previewed is by construction the number targeted. Every filter
 * combines with AND — "inactive 7 days AND spent over ৳1500" — because OR
 * trees are where marketing tools go to die.
 */

export const audienceFilterSchema = z.object({
  /** No completed order in this many days. */
  inactiveDays: z.number().int().min(1).max(365).optional(),
  /** Lifetime spend floor, integer BDT. */
  minSpentBdt: z.number().int().min(1).optional(),
  /** Completed-order floor. */
  minOrders: z.number().int().min(1).optional(),
  /** Favourite branch (FR-MKT-2 "visited Narayanganj"). */
  branchId: z.string().optional(),
  /** Membership in a computed segment (cheese_lover, coupon_hunter, …). */
  segment: z.enum(SEGMENT_NAMES).optional(),
  /** Has redeemed a coupon from this source ("used spinner coupon"). */
  usedCouponSource: z.enum(['spinner', 'campaign', 'referral', 'loyalty']).optional(),
  /** Birthday is today (Dhaka) — the occasion engine's daily trigger. */
  birthdayToday: z.boolean().optional(),
})

export type AudienceFilter = z.infer<typeof audienceFilterSchema>

/** A filter with nothing set would target everyone — require intent. */
export function assertFilterHasIntent(filter: AudienceFilter): void {
  if (Object.values(filter).every((value) => value === undefined)) {
    throw new Error('An audience needs at least one condition')
  }
}

/**
 * FR-MKT-6 — deterministic A/B assignment.
 *
 * Hashing the customer id means the same person always lands in the same
 * variant, across retries and re-sends — randomness at send time would let a
 * re-run reshuffle the experiment.
 */
export function abVariantFor(customerId: string, splitBp: number): 'a' | 'b' {
  if (splitBp <= 0) return 'a'
  let hash = 0
  for (let i = 0; i < customerId.length; i++) {
    hash = (hash * 31 + customerId.charCodeAt(i)) >>> 0
  }
  return hash % 10_000 < splitBp ? 'b' : 'a'
}
